import { HttpError, useOne } from "@refinedev/core";
import { CollapsibleSection } from "components/collapsibleSection";
import { Label } from "components/label";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import { Tag } from "components/tag";
import Textarea from "components/textarea";
import {
  IAttractionParticipantDetail,
  IAttractionParticipantForm,
} from "interfaces";
import { get } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { getFormattedDate, getFormattedDateTime } from "utills/helpers";

export const EditInformationSection = ({
  participantDetailData,
}: {
  participantDetailData: IAttractionParticipantDetail | null;
}) => {
  const { data: attendantTemplates, isLoading: isFetchingTemplates } = useOne<
    any[],
    HttpError
  >({
    resource: `participant/events/${participantDetailData?.event?.event_id}/attendant-templates/dropdown`,
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!participantDetailData?.event?.event_id,
    } as any,
  });

  const { data: categoryData, isFetching: isFetchingCategoryData } = useOne<
    any[],
    HttpError
  >({
    resource: `participant/events/${participantDetailData?.event?.event_id}/categories/dropdown`,
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!participantDetailData?.event?.event_id,
    } as any,
  });

  const {
    control,
    formState: { errors },
  } = useFormContext<IAttractionParticipantForm>(); // retrieve all hook methods

  const attendantTemplateIds = (attendantTemplates?.data ?? []).map(
    (template) => ({
      label: template.name,
      value: template.id,
    })
  );

  const categoryIds = (categoryData?.data ?? []).map(
    (category: { category_vi: string; category_en: string }) => ({
      value: category.category_en,
      label: category.category_en,
    })
  );

  return (
    <CollapsibleSection 
      label="Information"
      defaultOpen={true}
      additionalLabel={(
        <Tag text={participantDetailData?.participant.source ?? ""} customClassName="capitalize" type="blue" />
      )}
    >
      <div
        className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm sm:px-6 grid-cols-12 lg:gap-x-6"
        style={{ wordBreak: "break-word" }}
      >
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-1">
          <div className="col-span-2">
            <p className="font-medium">Customer code</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.customer_id}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-3">
          <div className="col-span-2">
            <p className="font-medium">Participant ID</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.id}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-5">
          <div className="col-span-2">
            <p className="font-medium">Seat</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.seat_number}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-7">
          <div className="col-span-2">
            <p className="font-medium">E-ticket usage date</p>
          </div>
          <div className="col-span-4">
            <p>
              {getFormattedDate(
                participantDetailData?.participant.ticket_use_for_date ?? ""
              )}
              {participantDetailData?.participant?.showtime
                ? ` - ${participantDetailData?.participant.showtime}`
                : ""}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-9">
          <div className="col-span-2">
            <p className="font-medium">Order code</p>
          </div>
          <div className="col-span-4">
            <Link
              to={`/orders/${participantDetailData?.participant.parent_ref_id}`}
              className="w-fit font-medium text-brand-500 hover:underline"
            >
              {participantDetailData?.participant.parent_ref_id}
            </Link>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-11">
          <div className="col-span-2">
            <p className="font-medium">Full name</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.full_name}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[13]">
          <div className="col-span-2">
            <Label
              id="attendant_template_id"
              name="Participant template name"
              required
            />
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="attendant_template_id"
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  key="attendant_template_id"
                  inputRef={ref}
                  options={attendantTemplateIds}
                  id="attendant_template_id"
                  placeholder="Select Template"
                  defaultValue={
                    attendantTemplateIds.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={attendantTemplateIds.find(
                    (selected: { label: string; value: string }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={errors["attendant_template_id"]}
                  isLoading={isFetchingTemplates}
                />
              )}
            />
            {errors["attendant_template_id"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["attendant_template_id"]?.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-2">
          <div className="col-span-2">
            <p className="font-medium">Event Name</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.event_name_en}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-4">
          <div className="col-span-2">
            <p className="font-medium">Ticket Name</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.ticket_name_en}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-6">
          <div className="col-span-2">
            <p className="font-medium">Ticket No</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.ticket_no}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-8">
          <div className="col-span-2">
            <p className="font-medium">
              <Label id="is_active" name="Status" />
            </p>
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="is_active"
              render={({ field: { onChange, value = true, ref } }) => (
                <SwitchButton checked={value} onChange={onChange} />
              )}
            />
            {errors["is_active"] ? (
              <p className="error-text">
                <span className="font-medium">{errors?.is_active.message}</span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-10">
          <div className="col-span-2">
            <Label id="category_en" name="Category" required />
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="category_en"
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  key="category_en"
                  inputRef={ref}
                  options={categoryIds}
                  id="category_en"
                  placeholder="Select Category"
                  defaultValue={
                    categoryIds.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={categoryIds.find(
                    (selected: { label: string; value: string }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={errors["category_en"]}
                  isLoading={isFetchingCategoryData}
                />
              )}
            />
            {errors["category_en"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["category_en"]?.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-12">
          <div className="col-span-2">
            <Label id="note" name="Note" />
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="note"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Textarea
                    id="note"
                    placeholder="Input note"
                    error={get(errors, `note`, "")}
                    onChange={onChange}
                    value={value}
                  />
                );
              }}
            />
            {errors["note"] ? (
              <p className="error-text">
                <span className="font-medium">{errors?.note.message}</span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[14]">
          <div className="col-span-2">
            <p className="font-medium">Created Date</p>
          </div>
          <div className="col-span-4">
            <p className="whitespace-pre-wrap">
              {getFormattedDateTime(participantDetailData?.participant.created_at ?? "")}
            </p>
          </div>
        </div>
        {participantDetailData?.participant.reference_code ? (
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[15]">
            <div className="col-span-2">
              <p className="font-medium">Reference Code</p>
            </div>
            <div className="col-span-4">
              <p className="whitespace-pre-wrap">
                {participantDetailData?.participant.reference_code}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </CollapsibleSection>
  );
};
